import { useEffect, useState } from "react";
import { FileText, Headphones, Video, Download } from "lucide-react";
import api from "@/lib/api";

const KIND_ICON = { video: Video, audio: Headphones, document: FileText };
const KIND_LABEL = { video: "Video", audio: "Audio", document: "PDF" };

export default function BrookeAccess() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/public/brooke")
            .then((r) => setGroups(r.data || []))
            .catch((e) => setErr(e.message || "Could not load resources"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-ink-50 text-ink-900" data-testid="brooke-page">
            <header className="border-b border-ink-200 bg-white">
                <div className="max-w-3xl mx-auto px-6 py-8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">HAEVN University · Open Access</div>
                    <h1 className="font-editorial text-3xl text-ink-900">Brooke&rsquo;s direct access</h1>
                    <p className="mt-2 text-sm text-ink-500 max-w-xl">
                        Open or download any module asset directly. No login required.
                    </p>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10 space-y-12">
                {loading && <div className="text-sm text-ink-500" data-testid="brooke-loading">Loading…</div>}
                {err && <div className="text-sm text-clay-500 bg-clay-50 border border-clay-50 px-3 py-2 rounded-sm" data-testid="brooke-error">{err}</div>}
                {!loading && !err && groups.length === 0 && (
                    <div className="text-sm text-ink-500">No resources available.</div>
                )}

                {groups.map((g) => (
                    <section key={g.id} data-testid={`brooke-module-${g.id}`}>
                        <div className="flex items-baseline justify-between mb-4">
                            <h2 className="font-heading text-xl text-ink-900">{g.title}</h2>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">{g.track_id?.replace(/-/g, " ")}</span>
                        </div>
                        <ul className="border border-ink-200 divide-y divide-ink-200 bg-white">
                            {g.items.map((item, idx) => {
                                const Icon = KIND_ICON[item.kind] || FileText;
                                return (
                                    <li key={idx} className="flex items-center gap-4 p-4">
                                        <Icon size={16} strokeWidth={1.5} className="text-ink-500 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-ink-900 truncate" data-testid={`brooke-item-title-${g.id}-${idx}`}>{item.title}</div>
                                            <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono mt-0.5">{KIND_LABEL[item.kind] || item.kind}</div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`brooke-item-open-${g.id}-${idx}`}
                                                className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-3 py-1.5 rounded-sm text-xs hover:bg-haevn-900"
                                            >
                                                Open
                                            </a>
                                            <a
                                                href={item.url}
                                                download
                                                data-testid={`brooke-item-download-${g.id}-${idx}`}
                                                className="inline-flex items-center gap-1.5 border border-ink-300 text-ink-900 px-3 py-1.5 rounded-sm text-xs hover:bg-ink-100"
                                            >
                                                <Download size={12} strokeWidth={1.5} /> Download
                                            </a>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                ))}
            </main>

            <footer className="border-t border-ink-200 mt-16">
                <div className="max-w-3xl mx-auto px-6 py-6 text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">
                    HAEVN University · Direct access
                </div>
            </footer>
        </div>
    );
}
